import React from 'react';
import {
  BookOpen,
  CreditCard,
  Car,
  Award,
  FileText,
  Landmark,
  Globe,
  UserCheck,
  Briefcase,
  Layers,
  FolderPlus,
  ArrowRight
} from 'lucide-react';
import { INITIAL_CATEGORIES, subscribeToCategoriesFromFirestore, CategoryModel } from '../lib/categoriesService';
import { useState, useEffect } from 'react';
import { DocumentCategory, Product } from '../types';
import { getCategoryPublishedCount } from '../lib/statsService';

interface CategoriesSectionProps {
  selectedCategory: DocumentCategory | 'All';
  setSelectedCategory: (cat: DocumentCategory | 'All') => void;
  darkMode: boolean;
  onNavigateCategory?: (category: DocumentCategory) => void;
  products?: Product[];
}

export const CategoriesSection: React.FC<CategoriesSectionProps> = ({

  selectedCategory,
  setSelectedCategory,
  darkMode,
  onNavigateCategory,
  products = [],
}) => {
  const [categoriesList, setCategoriesList] = useState<CategoryModel[]>(INITIAL_CATEGORIES);
  useEffect(() => {
    const unsub = subscribeToCategoriesFromFirestore((cats) => {
      setCategoriesList(cats.filter(c => c.status !== 'hidden').sort((a,b)=> (a.displayOrder||0) - (b.displayOrder||0)));
    });
    return () => unsub();
  }, []);
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'BookOpen': return <BookOpen className="w-6 h-6" />;
      case 'CreditCard': return <CreditCard className="w-6 h-6" />;
      case 'Car': return <Car className="w-6 h-6" />;
      case 'Award': return <Award className="w-6 h-6" />;
      case 'FileText': return <FileText className="w-6 h-6" />;
      case 'Landmark': return <Landmark className="w-6 h-6" />;
      case 'Globe': return <Globe className="w-6 h-6" />;
      case 'UserCheck': return <UserCheck className="w-6 h-6" />;
      case 'Briefcase': return <Briefcase className="w-6 h-6" />;
      case 'Layers': return <Layers className="w-6 h-6" />;
      default: return <FolderPlus className="w-6 h-6" />;
    }
  };

  return (
    <section className="py-12 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-400"></span>
              <span className="text-xs font-bold uppercase tracking-widest text-cyan-400">Browse By Category</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Explore Premium Document Categories
            </h2>
          </div>

          <button
            onClick={() => setSelectedCategory('All')}
            className={`text-xs font-extrabold px-4 py-2 rounded-xl border transition-all flex items-center gap-1.5 ${
              selectedCategory === 'All'
                ? 'bg-blue-600 text-white border-blue-500 shadow-md shadow-blue-500/20'
                : darkMode
                ? 'bg-slate-800/60 border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700/60'
                : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <span>View All Templates</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* 3D Glass Category Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {categoriesList.map((cat) => {
            const isSelected = selectedCategory === cat.name;

            return (
              <div
                key={cat.name}
                onClick={() => {
                  if (onNavigateCategory) {
                    onNavigateCategory(cat.name as DocumentCategory);
                  } else {
                    setSelectedCategory(cat.name as DocumentCategory);
                  }
                }}
                className={`group relative rounded-2xl p-4 cursor-pointer transition-all duration-[400ms] border ${
                  isSelected
                    ? 'bg-gradient-to-b from-blue-900/60 via-indigo-900/40 to-slate-900/80 border-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.3)] -translate-y-1'
                    : darkMode
                    ? 'glass-card border-white/10 hover:border-cyan-400/80 hover:shadow-[0_0_25px_rgba(34,211,238,0.3),inset_0_0_12px_rgba(34,211,238,0.15)] md:hover:-translate-y-1.5'
                    : 'glass-card-light border-slate-200 hover:border-cyan-400/80 hover:shadow-[0_0_25px_rgba(34,211,238,0.3),inset_0_0_12px_rgba(34,211,238,0.15)] md:hover:-translate-y-1.5'
                }`}
              >
                {/* Accent Color Glow */}
                <div
                  className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-0 group-hover:opacity-50 transition-opacity duration-[400ms] blur-[30px] pointer-events-none"
                  style={{ backgroundColor: cat.accentColor }}
                />

                <div className="flex flex-col h-full justify-between gap-3">
                  {/* Icon with gradient background */}
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-tr ${cat.gradient} p-0.5 text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <div className="w-full h-full rounded-[10px] bg-slate-950/40 flex items-center justify-center">
                      {getIcon(cat.iconName)}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-bold text-sm tracking-tight group-hover:text-cyan-300 transition-all duration-[400ms] group-hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]">
                      {cat.name}
                    </h3>
                    <p className="text-[11px] font-semibold text-cyan-400/80 mt-0.5">
                      {getCategoryPublishedCount(products, cat.name)} Templates
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};
